import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { searchUsers, createChat } from '../api/chats';
import { ApiError } from '../api/client';
import { backdropClose } from '../lib/modalBackdrop';

interface FoundUser {
  id: string;
  username: string;
  displayName: string;
}

interface Props {
  onClose: () => void;
  onCreated: (chatId: string) => void;
  /** С какого типа открыть — выбор уже сделан в меню FAB перед этим (см. ChatList), но таб
   *  переключения остаётся в форме: можно передумать, не закрывая её заново. */
  initialType?: 1 | 2;
}

export default function NewChat({ onClose, onCreated, initialType = 1 }: Props) {
  const { t } = useTranslation('people');
  const [type, setType] = useState<1 | 2>(initialType);
  const [title, setTitle] = useState('');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<FoundUser[]>([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<FoundUser[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const doSearch = async () => {
    if (!q.trim() || searching) return;
    setSearching(true);
    try {
      const r = await searchUsers(q.trim());
      setResults(r.filter((u) => !selected.some((s) => s.id === u.id)));
      setSearched(true);
    } catch {
      /* ignore */
    } finally {
      setSearching(false);
    }
  };

  const toggle = (u: FoundUser) => {
    setSelected((prev) => {
      if (prev.some((s) => s.id === u.id)) return prev.filter((s) => s.id !== u.id);
      // Личный чат — ровно один собеседник: выбор нового заменяет предыдущий, а не копится
      // молча за disabled-кнопкой без объяснения, почему "Создать" не нажимается.
      return type === 1 ? [u] : [...prev, u];
    });
    setResults((prev) => prev.filter((r) => r.id !== u.id));
  };

  const canCreate = type === 1 ? selected.length === 1 : title.trim().length > 0 && selected.length > 0;

  // Создание выполняется только явным подтверждением формы.
  const create = async () => {
    if (creating || !canCreate) return;
    setCreating(true);
    setError('');
    try {
      const chat = await createChat(type, type === 2 ? title.trim() : null, selected.map((s) => s.id));
      onCreated(chat.id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('newChat.createError'));
      setCreating(false);
    }
  };

  return (
    <div className="modal-backdrop" {...backdropClose(onClose)}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" title={t('newChat.close')} onClick={onClose} disabled={creating}>✕</button>
        <h2>{t('newChat.title')}</h2>
        <button type="button" className="btn" onClick={create} disabled={!canCreate || creating}>{t('newChat.create')}</button>
        {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
        <div className="tabs">
          <button className={'tab' + (type === 1 ? ' active' : '')} onClick={() => setType(1)}>
            {t('newChat.personal')}
          </button>
          <button className={'tab' + (type === 2 ? ' active' : '')} onClick={() => setType(2)}>
            {t('newChat.group')}
          </button>
        </div>

        {type === 2 && (
          <input
            className="input"
            placeholder={t('newChat.groupName')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ marginBottom: 8 }}
          />
        )}

        <div className="row" style={{ marginBottom: 8 }}>
          <input
            className="input"
            placeholder={t('newChat.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          />
          <button className="btn" onClick={doSearch} disabled={searching}>{searching ? '…' : t('newChat.find')}</button>
        </div>

        {searched && !searching && results.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: 13, margin: '4px 0 8px' }}>{t('newChat.empty')}</div>
        )}
        {results.map((u) => (
          <div key={u.id} className="user-row" onClick={() => toggle(u)}>
            <div className="avatar" style={{ width: 32, height: 32, fontSize: 13 }}>
              {u.displayName[0]?.toUpperCase()}
            </div>
            <div>
              <div>{u.displayName}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>@{u.username}</div>
            </div>
          </div>
        ))}

        {selected.length > 0 && (
          <div style={{ margin: '8px 0' }}>
            {selected.map((u) => (
              <span key={u.id} className="chip">
                {u.displayName}
                <button onClick={() => toggle(u)}>✕</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
